import { AssetGroup } from '@play-co/timestep-core';
import { FlexBoxView, getFlexNode } from '@play-co/timestep-core/flexbox';
import { View, ImageScaleView } from '@play-co/timestep-core/ui';
import { downscale } from 'src/conf/scale';
import { Avatar } from 'src/game/components/shared/Avatar';
import Scroll from 'src/game/components/shared/Scroll';
import PopupBasic from 'src/game/components/popups/PopupBasic';
import StateObserver from 'src/StateObserver';
import bitmapFonts from 'src/lib/bitmapFonts';
import i18n from 'src/lib/i18n/i18n';
import sounds from 'src/lib/sounds';
import { isShareTargetPickerAvailable } from 'src/lib/sharing';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import uiConfig from 'src/lib/ui/config';
import { Feature } from 'src/replicant/analytics';
import type { CardsCandidate } from 'src/replicant/cards/cards.asyncGetters';
import type { CardId } from 'src/replicant/cards/cards.ruleset';
import { cardsShareTrade } from '../cards.share';
import { CardsEmptyMessage } from '../components/CardsEmptyMessage';

const SHARE_BUTTON_WIDTH = downscale(396);
const SHARE_BUTTON_HEIGHT = downscale(133);

export default class PopupCardTradeProposeList extends PopupBasic<
  (candidateId?: string) => void
> {
  private scroll: Scroll<CardsCandidate>;
  private emptyMessage: CardsEmptyMessage;
  private btnLine: ButtonScaleView;
  private btnOthers: ButtonScaleView;
  private cardId?: CardId;

  constructor(opts: {
    superview: View;
    close: (candidateId?: string) => void;
  }) {
    super({
      superview: opts.superview,
      close: opts.close,
      zIndex: 10000,
      width: 582,
      height: 720,
      darkerBg: true,
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.SEND,
      },
    });

    const scrollWidth = 535;
    const locale = StateObserver.getLocale();
    this.scroll = new Scroll<CardsCandidate>({
      superview: this.box,
      createItem: this.createItem.bind(this),
      showBg: true,
      firstItemOffset: 5,
      margin: 7,
      rect: {
        x: (this.box.style.width - scrollWidth) * 0.5,
        y: 80,
        width: scrollWidth,
        height: 500,
      },
    });

    this.emptyMessage = new CardsEmptyMessage({
      superview: this.box,
      localeText: () => i18n('cards.cardTrade.noTradeCandidatesMessage'),
    });
    this.emptyMessage.hide();

    const buttonRow = new FlexBoxView({
      superview: this.box,
      x: 0,
      y: this.box.style.height - SHARE_BUTTON_HEIGHT - 33,
      width: this.box.style.width,
      height: SHARE_BUTTON_HEIGHT,
      flex: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
      },
    });

    this.btnOthers = new ButtonScaleView({
      superview: buttonRow,
      ...uiConfig.buttons.blue,
      width: SHARE_BUTTON_WIDTH,
      height: SHARE_BUTTON_HEIGHT,
      icon: {
        image: `assets/ui/shared/buttons/btn_others_${locale}.png`,
        width: downscale(362),
        height: downscale(102),
        offset: { x: 5, y: -5 },
      },
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.TRADE,
      },
      onClick: async () => {
        sounds.sfx.play('ok');
        await this.shareTrade('others');
      },
    });

    this.btnLine = new ButtonScaleView({
      superview: buttonRow,
      ...uiConfig.buttons.secondary,
      width: SHARE_BUTTON_WIDTH,
      height: SHARE_BUTTON_HEIGHT,
      icon: {
        image: `assets/ui/shared/buttons/btn_line_${locale}.png`,
        width: downscale(365),
        height: downscale(102),
        offset: { x: 5, y: -5 },
      },
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.TRADE,
      },
      onClick: async () => {
        sounds.sfx.play('ok');
        await this.shareTrade('line');
      },
    });

    for (const child of buttonRow.getSubviews()) {
      getFlexNode(child).updateStyle({
        marginLeft: 5,
        marginRight: 5,
      });
    }
  }

  protected getAssetsGroup() {
    const locale = StateObserver.getLocale();
    return new AssetGroup([
      'assets/ui/shared/frames/scroller_panel_blue.png',
      `assets/ui/shared/buttons/btn_line_${locale}.png`,
      `assets/ui/shared/buttons/btn_others_${locale}.png`,
      ...AssetGroup.constructURLs(['assets/ui/cards/common']),
    ]);
  }

  private createItem(superview: View, _: number, item: CardsCandidate) {
    const width = 510;
    const height = 95;

    const view = new View({
      tag: 'item',
      superview,
      width,
      height,
    });

    new ImageScaleView({
      tag: 'frame',
      superview: view,
      image: 'assets/ui/shared/frames/scroller_panel_blue.png',
      scaleMethod: '9slice',
      width,
      height,
      x: (superview.style.width - width) * 0.5,
      sourceSlices: {
        vertical: { top: 35, bottom: 35 },
        horizontal: { left: 35, right: 35 },
      },
    });

    new Avatar({
      superview: view,
      x: 32,
      y: 10,
      width: 68,
      height: 68,
      centerOnOrigin: false,
      image: item.photo,
    });

    new LangBitmapFontTextView({
      tag: 'friend-name',
      superview: view,
      align: 'left',
      verticalAlign: 'center',
      color: '#9A7171',
      wordWrap: false,
      x: 108,
      y: 25,
      size: 28,
      width: 225,
      height: 35,
      font: bitmapFonts('Body'),
      localeText: () => item.name ?? '',
    });

    const button = new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      font: bitmapFonts('Body'),
      fontSize: 23,
      labelOffsetX: -3,
      labelOffsetY: -2,
      x: 510 - 130,
      y: 12,
      width: 124,
      height: 66,
      lineSpacing: -14,
      localeText: () => i18n('cards.cardTrade.propose'),
      onClick: async () => {
        sounds.sfx.play('ok');
        this.opts.close(item.id);
      },
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.SEND,
      },
    });
    view.addSubview(button);

    return view;
  }

  public init({
    candidates,
    cardId,
  }: {
    candidates: CardsCandidate[];
    cardId: CardId;
  }) {
    super.init({
      title: i18n('cards.cardTrade.proposeTitle'),
    });

    this.cardId = cardId;
    this.scroll.setItems(candidates);
    this.updateInviteButtonVisibility();
    this.updateEmptyState(candidates.length === 0);
  }

  private updateEmptyState(empty: boolean) {
    if (empty) {
      this.scroll.getView().hide();
      this.scroll.getBackgroundView()?.hide();
      this.emptyMessage.show();
      return;
    }

    this.scroll.getView().show();
    this.scroll.getBackgroundView()?.show();
    this.emptyMessage.hide();
  }

  private updateInviteButtonVisibility() {
    if (isShareTargetPickerAvailable()) {
      this.btnLine.show();
    } else {
      this.btnLine.hide();
    }
  }

  private async shareTrade(method: 'line' | 'others') {
    const { cardId } = this;
    if (!cardId) return;

    await cardsShareTrade({ cardId, preferredMode: method });
  }
}
